import { supabase } from "./supabase";

const raise = (error) => {
  if (error) throw new Error(error.message);
};

// ---------------- AUTH ----------------

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error("Invalid credentials");
  return data.user;
};

export const signOut = () => supabase.auth.signOut();

export const getSession = async () => {
  const { data } = await supabase.auth.getSession();
  return data.session;
};

export const changePassword = async (currentPassword, newPassword) => {
  const session = await getSession();
  if (!session) throw new Error("Session expired. Please login again.");

  const { error: reauthError } = await supabase.auth.signInWithPassword({
    email: session.user.email,
    password: currentPassword,
  });
  if (reauthError) throw new Error("Current password is incorrect");

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  raise(error);
};

// ---------------- SHIPMENTS ----------------

export const listShipments = async ({ search = "", status = "", page = 1, limit = 15 }) => {
  let query = supabase.from("shipments").select("*", { count: "exact" });
  if (search.trim()) query = query.ilike("docket_number", `%${search.trim()}%`);
  if (status) query = query.eq("current_status", status);

  const from = (page - 1) * limit;
  const { data, count, error } = await query
    .order("created_at", { ascending: false })
    .range(from, from + limit - 1);
  raise(error);

  const total = count || 0;
  return { shipments: data || [], total, pages: total > 0 ? Math.ceil(total / limit) : 1 };
};

export const statusCounts = async () => {
  const { data, error } = await supabase.from("shipments").select("current_status");
  raise(error);
  return (data || []).reduce((acc, row) => {
    acc[row.current_status] = (acc[row.current_status] || 0) + 1;
    return acc;
  }, {});
};

export const createShipment = async (form) => {
  const docket = form.docket_number.trim();
  const { data: existing } = await supabase
    .from("shipments")
    .select("id")
    .eq("docket_number", docket)
    .maybeSingle();
  if (existing) throw new Error("Docket number already exists");

  const { error } = await supabase.from("shipments").insert({
    ...form,
    docket_number: docket,
    current_status: "Booked",
    remarks: form.remarks || null,
    tracking_history: [
      {
        status: "Booked",
        date_time: new Date().toISOString(),
        remarks: `Shipment booked from ${form.origin_city} to ${form.destination_city}`,
        location: form.origin_city,
      },
    ],
  });
  raise(error);
};

export const updateShipment = async (id, form) => {
  const { error } = await supabase.from("shipments").update(form).eq("id", id);
  raise(error);
};

export const updateShipmentStatus = async (id, { status, remarks, location }) => {
  const { error } = await supabase.rpc("update_shipment_status", {
    p_id: id,
    p_status: status,
    p_remarks: remarks || "",
    p_location: location || "",
  });
  raise(error);
};

export const deleteShipment = async (id) => {
  const { error } = await supabase.from("shipments").delete().eq("id", id);
  raise(error);
};

export const trackShipment = async (docketNumber) => {
  const { data, error } = await supabase.rpc("track_shipment", { p_docket: docketNumber });
  raise(error);
  return data && data.length > 0 ? data[0] : null;
};

// ---------------- PICKUP REQUESTS ----------------

export const listPickupRequests = async ({ status = "", page = 1, limit = 15 }) => {
  let query = supabase.from("pickup_requests").select("*", { count: "exact" });
  if (status) query = query.eq("status", status);

  const from = (page - 1) * limit;
  const { data, count, error } = await query
    .order("created_at", { ascending: false })
    .range(from, from + limit - 1);
  raise(error);

  const total = count || 0;
  return { requests: data || [], total, pages: total > 0 ? Math.ceil(total / limit) : 1 };
};

export const createPickupRequest = async (form) => {
  const { error } = await supabase.from("pickup_requests").insert({ ...form, status: "New" });
  raise(error);
};

export const updatePickupStatus = async (id, status) => {
  const { error } = await supabase.from("pickup_requests").update({ status }).eq("id", id);
  raise(error);
};

export const deletePickupRequest = async (id) => {
  const { error } = await supabase.from("pickup_requests").delete().eq("id", id);
  raise(error);
};

// ---------------- CONTACT INQUIRIES ----------------

export const createInquiry = async (form) => {
  const { error } = await supabase.from("inquiries").insert(form);
  raise(error);
};
